import React, { memo, useCallback, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { postOrderDetails } from '@yme/api';
import { Alert, AlertType } from '@yme/atoms/alert';
import { BrandOverline } from '@yme/atoms/brand-overline';
import { SIZE_44, CONTAINED_VARIANT, Button, NO_ICON } from '@yme/atoms/button';
import { Container, Row, Col } from '@yme/atoms/grid';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { FormField, FormFieldType } from '@yme/molecules/form-field';
import { getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorAccentAlert900, colorAccentError900, colorCoreBlack300, colorCoreBlack900Base, colorCoreWhite1000, } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularLinkLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { DigitalDataMutation, getSanitizeHtml, setDigitalData } from '@yme/utils/common';
import { regExpValidationEmail } from '@yme/utils/regEx';
import { StepRoutes, FormikFields } from '../constants';
import { RichTextContainerStyled } from './OrderStepStyled';
import { ContentContainerStyled, FlexWrapperStyled, InfoSectionContainerStyled } from './styled';
var OrderError;
(function (OrderError) {
    OrderError["UNKNOWN_ORDER"] = "UNKNOWN_ORDER";
    OrderError["CANNOT_BE_RETURNED"] = "CANNOT_BE_RETURNED";
})(OrderError || (OrderError = {}));
export const OrderStep = memo(({ stepData: { returnPolicy, showDisclaimer }, locale, commercetoolsProject, cachedData: { orderNumber, email }, redirectFormPageUrl, supportLink, setCachedData, }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const { isMobileView } = useWindowSize();
    const isRightToLeft = useSelector(getRTL);
    const [isLoading, setIsLoading] = useState(false);
    const [orderError, setOrderError] = useState(null);
    const formikInitialValues = {
        [FormikFields.ORDER_NUMBER]: orderNumber || '',
        [FormikFields.EMAIL_ADDRESS]: email || '',
    };
    const formikValidationSchema = Yup.object().shape({
        [FormikFields.ORDER_NUMBER]: Yup.string().required(' '),
        [FormikFields.EMAIL_ADDRESS]: Yup.string()
            .required(' ')
            .matches(regExpValidationEmail, t('returnForm_yourOrderEmail_error')),
    });
    const handleSubmit = useCallback(async ({ [FormikFields.ORDER_NUMBER]: orderNumber, [FormikFields.EMAIL_ADDRESS]: email }) => {
        try {
            setIsLoading(true);
            const orderDetails = await postOrderDetails(orderNumber, email, commercetoolsProject, locale);
            // order not found
            if (!orderDetails.id) {
                setOrderError(OrderError.UNKNOWN_ORDER);
            }
            else if (isEmpty(orderDetails.deliveries)) {
                setOrderError(OrderError.CANNOT_BE_RETURNED);
            }
            else {
                setCachedData(() => ({
                    orderDetails,
                    orderNumber,
                    email,
                }));
                // order is OK
                setOrderError(null);
                history.push(`/${StepRoutes.PACKAGE_STEP}`);
            }
        }
        catch (error) {
        }
        finally {
            setIsLoading(false);
        }
    }, [commercetoolsProject, history, locale, setCachedData]);
    useEffect(() => {
        setDigitalData(DigitalDataMutation.RETURN_PAGE_VIEW, null);
    }, []);
    return (React.createElement(Formik, { initialValues: formikInitialValues, validationSchema: formikValidationSchema, onSubmit: handleSubmit, validateOnMount: true }, ({ isValid }) => (React.createElement(Form, null,
        React.createElement(FlexWrapperStyled, { flexDirection: "column" },
            showDisclaimer && (React.createElement(Container, { bgColor: "transparent", width: "100%" },
                React.createElement(Row, { justifyContent: "center" },
                    React.createElement(Col, { lg: 8 },
                        React.createElement(Alert, { isFullWidth: true, hideExtraContent: true, message: React.createElement(React.Fragment, null,
                                t('returnForm_formType_alert'),
                                ' ',
                                React.createElement(RegularLinkLevel2, { href: redirectFormPageUrl, color: colorAccentAlert900 }, t('returnForm_formType_link'))), type: AlertType.WARNING, isRightToLeft: isRightToLeft }))))),
            React.createElement(ContentContainerStyled, { bgColor: "transparent", width: "100%" },
                React.createElement(Row, { justifyContent: "center" },
                    React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                        React.createElement(BrandOverline, { borderSize: "6px", borderColor: colorCoreBlack900Base },
                            React.createElement(InfoSectionContainerStyled, null,
                                React.createElement(HeadingLevel3, null, t('returnForm_yourOrder_header')),
                                React.createElement(FlexWrapper, { mt: "8px" },
                                    React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_yourOrder_description'))),
                                orderError && (React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                    React.createElement(Alert, { type: AlertType.ERROR, message: orderError === OrderError.CANNOT_BE_RETURNED
                                            ? t('returnForm_yourOrderCannotReturn_error')
                                            : t('returnForm_yourOrderValidation_error'), action: orderError === OrderError.CANNOT_BE_RETURNED && supportLink ? (React.createElement(RegularLinkLevel2, { href: supportLink, color: colorAccentError900 }, t('returnForm_yourOrderSupport_link'))) : null, dataTestId: "ReturnForm-AlertError-yourOrderExpired", isRightToLeft: isRightToLeft }))),
                                React.createElement(FlexWrapper, { mt: "32px" },
                                    React.createElement(FormField, { fieldType: FormFieldType.INPUT, id: FormikFields.ORDER_NUMBER, name: FormikFields.ORDER_NUMBER, label: t('returnForm_yourOrderOrderNumber_label'), placeholder: t('returnForm_yourOrderOrderNumber_placeholder'), type: "text", isRightToLeft: isRightToLeft, isDisabled: false, isRequired: true })),
                                React.createElement(FlexWrapper, { mt: "32px" },
                                    React.createElement(FormField, { fieldType: FormFieldType.INPUT, id: FormikFields.EMAIL_ADDRESS, name: FormikFields.EMAIL_ADDRESS, label: t('returnForm_yourOrderEmail_label'), placeholder: t('returnForm_yourOrderEmail_placeholder'), type: "text", isRightToLeft: isRightToLeft, isDisabled: false, isRequired: true })),
                                React.createElement(FlexWrapper, { mt: "32px" },
                                    React.createElement(Button, { disabled: !isValid, type: "submit", buttonVariant: CONTAINED_VARIANT, label: t('returnForm_yourOrder_button'), width: isMobileView ? '100%' : 'auto', hasIconButton: NO_ICON, size: SIZE_44, isLoading: isLoading }))))))),
            React.createElement(ContentContainerStyled, { width: "100%", bgColor: "transparent" },
                React.createElement(Row, { justifyContent: "center" },
                    React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                        React.createElement(InfoSectionContainerStyled, null,
                            React.createElement(HeadingLevel3, null, t('returnForm_yourOrderPolicy_header')),
                            React.createElement(FlexWrapper, { mt: "8px" },
                                React.createElement(RichTextContainerStyled, { dangerouslySetInnerHTML: {
                                        __html: getSanitizeHtml(returnPolicy),
                                    } })))))))))));
});
OrderStep.displayName = 'YourOrderStep_ReturnForm';
